<template>
  <v-container>
    <v-card>
      <v-card-title class="mb-0">
        {{ $t('platform-usage.title') }}
        <v-spacer />
      </v-card-title>
      <v-progress-linear
        :aria-label="$t('platform-usage.loading')"
        :active="busy"
        indeterminate
      />
      <div id="platform-usage-content">
        <v-card-text id="total-sessions" class="mt-5">
          {{ $t('platform-usage.sessions-started', [usage.totalSessions]) }}
        </v-card-text>
        <v-row>
          <v-col id="platform-usage-column-chart">
            <google-chart
              v-if="usage.loaded"
              type="ColumnChart"
              :data="usage.sessions.chartData"
              :options="usage.sessions.chartOptions"
            />
          </v-col>
        </v-row>
        <v-row v-if="usage.loaded && allModules.length > 0">
          <v-col>
            <v-card>
              <v-card-title>
                {{ $t('platform-usage.all-modules') }}
              </v-card-title>
              <v-card-text>
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">
                          {{ $t('platform-usage.module-name') }}
                        </th>
                        <th class="text-left">
                          {{ $t('platform-usage.session-count') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="module in paginatedModules" :key="module.name">
                        <td>{{ module.name }}</td>
                        <td>{{ module.sessions }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <v-pagination
                  v-if="totalPages > 1"
                  v-model="currentPage"
                  :length="totalPages"
                  :total-visible="7"
                  class="mt-4"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row v-if="usage.sessions" justify="center" class="d-lg-none">
          <v-col>
            <tbody>
            <tr v-for="(legend, index) in mobileLegendsOfPlatformUsage" :key="index">
              <td style="padding-left: 15px;">
                <v-list-item-avatar
                  tile
                  size="10"

                  class="align-self-center"
                  :color="usage.sessions.chartOptions.colors[index]"
                />{{ legend }}
              </td>
            </tr>
            </tbody>
          </v-col>
        </v-row>
        <v-row id="device-usage" style="">
          <v-col>
            <google-chart
              v-if="usage.loaded"
              type="PieChart"
              :data="usage.devices.chartData"
              :options="usage.devices.chartOptions"
            />
          </v-col>
          <v-col v-if="usage.devices" class="d-none d-lg-block" style="padding-top: 53px;">
            <tbody>
            <tr v-for="(legend, index) in legendsOfDeviceUsage" :key="index">
              <td style="padding-right: 10px; padding-left: 10px">
                <v-list-item-avatar
                  tile
                  size="10"
                  :color="usage.devices.chartOptions.colors[index]"
                />{{ legend }}
              </td>
            </tr>
            </tbody>
          </v-col>
        </v-row>
        <v-row v-if="usage.devices" justify="center" class="d-lg-none">
          <v-col>
            <tbody>
            <tr v-for="(legend, index) in legendsOfDeviceUsage" :key="index">
              <td style="padding-left: 15px;">
                <v-list-item-avatar
                  tile
                  size="10"

                  class="align-self-center"
                  :color="usage.sessions.chartOptions.colors[index]"
                />{{ legend }}
              </td>
            </tr>
            </tbody>
          </v-col>
        </v-row>
      </div>
    </v-card>
  </v-container>
</template>

<script>
  import api from '@/helpers/api.js'
  import { GChart } from 'vue-google-charts'
  import { Roles } from '@/helpers/users.js'
  import { reportScopes } from '@/helpers/reportScopes.js'
  import moment from 'moment'
  import { ContentLibrary } from '@/helpers/contentLibrary.js'
  
  export default {
    name: 'PlatformUsage',

    components: {
      'google-chart': GChart,
    },
    props: {
      startDate: { type: Date, required: true, default: null },
      endDate: { type: Date, required: true, default: null },
      scope: { type: String, required: true, default: 'account' },
    },

    data: function () {
      return {
        busy: false,
        dates: [moment().startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')],
        legendsOfPlatformUsage: [],
        legendsOfDeviceUsage: [],
        mobileLegendsOfPlatformUsage: [],
        currentPage: 1,
        pageSize: 10,
        usage: {
          loaded: false,
          sessions: {
            chartData: undefined,
            chartOptions: {
              title: this.$t('platform-usage.sessions'),
              height: 500,
              width: '100%',
              isStacked: true,
              legend: { position: 'none' },
              fontName: 'Inter',
              colors: ['#003F5C', '#58508D', '#BC5090', '#FF6361', '#FFA600', '#86C2A2', '#F2E1AE', '#FC92B7', '#976A9E', '#5E5B8A', '#EB6486', '#3C84FE', '#F3E783', '#2FC338', '#FEAE65', '#64C2A6', '#E6F69D', '#AADEA7'],
            },
          },
          devices: {
            chartData: undefined,
            chartOptions: {
              height: 350,
              width: '100%',
              legend: { position: 'none' },
              title: this.$t('platform-usage.devices'),
              fontName: 'Inter',
              colors: ['#00008B', '#57167E', '#1F75FE', '#2B0B3F', '#FFF1C9', '#F7B7A3', '#57167E', '#EA5F89', '#2D2D2D', '#658da6', '#ff9416', '#3f3b73', '#ffa758', '#192e40', '#FEAE65', '#64C2A6', '#E6F69D', '#AADEA7'],
            },
          },
          moduleTotals: {
            chartData: undefined,
            chartOptions: {
              height: 350,
              width: '100%',
              legend: { position: 'none' },
              title: this.$t('platform-usage.modules'),
              fontName: 'Inter',
              colors: ['#003F5C', '#58508D', '#BC5090', '#FF6361', '#FFA600', '#86C2A2', '#F2E1AE', '#FC92B7', '#976A9E', '#5E5B8A', '#EB6486', '#3C84FE', '#F3E783', '#2FC338', '#FEAE65', '#64C2A6', '#E6F69D', '#AADEA7'],
            },
          },
        },
      }
    },

    computed: {
      account () {
        if (this.$route.query.account) {
          return this.$route.query.account
        } else {
          return this.$store.getters.currentUserProfile.user.account
        }
      },
      period () {
        return {
          start: this.startDate,
          end: this.endDate,
        }
      },
      colorSelector () {
        return this.chunk(this.usage.sessions.chartOptions.colors, 2)
      },
      allModules () {
        if (!this.usage.moduleTotals.chartData || this.usage.moduleTotals.chartData.length <= 1) {
          return []
        }

        // Skip the header row (index 0) and get the data rows
        const moduleData = this.usage.moduleTotals.chartData.slice(1)

        // Sort by session count (index 1) in descending order
        return moduleData
          .sort((a, b) => b[1] - a[1])
          .map(module => ({
            name: module[0],
            sessions: module[1],
          }))
      },
      paginatedModules () {
        const startIndex = (this.currentPage - 1) * this.pageSize
        const endIndex = startIndex + this.pageSize
        return this.allModules.slice(startIndex, endIndex)
      },
      totalPages () {
        return Math.ceil(this.allModules.length / this.pageSize)
      },
    },

    watch: {
      async period () {
        await this.refreshItems()
      },
      // Watch for ContentLibrary changes and re-format data when it becomes available
      '$store.state.contentLibrary': {
        handler (newContentLibrary) {
          if (newContentLibrary && this.usage.loaded) {
            // Re-format the data with proper module names
            this.reformatDataWithContentLibrary()
          }
        },
        immediate: false,
      },
    },

    async mounted () {
      try {
        // Ensure ContentLibrary is loaded first
        await ContentLibrary.Get(this.$store, this.$i18n.locale)
        await this.updateUsage()
      } catch (error) {
        console.error('Error loading PlatformUsage component:', error)
        // Still try to load usage data even if ContentLibrary fails
        await this.updateUsage()
      }
    },
    methods: {
      async refreshItems () {
        this.busy = true
        try {
          await this.updateUsage()
        } catch (e) {
          console.error(e)
        }

        this.busy = false
      },
      async updateUsage () {
        this.busy = true
        let usageReport = null
        console.log('Usage Data: fetching')
        try {
          switch (this.scope) {
            case reportScopes.GLOBAL:
              usageReport = await api.getGlobalUsageReport(this.startDate, this.endDate, Roles.SYSADMIN)
              break
            case reportScopes.CLASS:
              usageReport = await api.getClassUsageReport(this.startDate, this.endDate, this.$route.params.id, this.account)
              break
            case reportScopes.ACCOUNT:
              usageReport = await api.getUsageReport(this.startDate, this.endDate, 'module', this.account)
              break
          }
        } catch (e) {
          console.error(e)
        }
        console.log('Usage Data: downloaded')
        
        if (usageReport) {
          this.usage.totalSessions = usageReport.totalSessions
          const legendsOfDeviceUsage = usageReport.deviceTotals.map((deviceTotal) => {
            return deviceTotal[0]
          })
          legendsOfDeviceUsage.shift()
          this.legendsOfDeviceUsage = legendsOfDeviceUsage
          this.usage.devices.chartData = usageReport.deviceTotals
          const legends = this.formatDialyUsageData(usageReport.dailyUsage[0])
          usageReport.dailyUsage[0] = legends
          const [, ...renderLegends] = legends
          // console.log({ renderLegends })
          this.mobileLegendsOfPlatformUsage = renderLegends
          const arrayOfLegends = this.chunk(renderLegends, 2)
          this.legendsOfPlatformUsage = arrayOfLegends
          this.usage.sessions.chartData = usageReport.dailyUsage
          console.log('Sessions chartData after assignment:', this.usage.sessions.chartData)
          this.usage.moduleTotals.chartData = this.formatModuleTotals(usageReport.moduleTotals)
          this.$store.dispatch('setTotalClassSessions', usageReport.totalSessions)
          this.$store.dispatch('setClassTrainingMinutes', usageReport.trainingMinutes)
          this.$store.dispatch('setActiveClassUsers', usageReport.activeUniqueUsers)
          this.$store.dispatch('setNewClassUsers', usageReport.newUserCount)
        }
        this.usage.loaded = true
        this.busy = false
      },
      formatDialyUsageData (arrayOfTitles) {
        const translatedModuleTitles = ['date']
        arrayOfTitles.forEach((moduleTitle) => {
          if (moduleTitle !== arrayOfTitles[0]) {
            try {
              translatedModuleTitles.push(ContentLibrary.NameOf(this.$store, this.$i18n.locale, moduleTitle))
            } catch (error) {
              console.warn('Error translating module title:', moduleTitle, error)
              translatedModuleTitles.push(moduleTitle)
            }
          }
        })
        return translatedModuleTitles
      },
      formatModuleTotals (moduleTotals) {
        const filtered = moduleTotals.filter((moduleTotal, index) => {
          // Keep header row (index 0) and entries with sessions > 0
          return index === 0 || moduleTotal[1] > 0
        })

        console.log('Filtered moduleTotals:', filtered)

        filtered.forEach((moduleTotal) => {
          if (filtered.indexOf(moduleTotal) !== 0) {
            try {
              moduleTotal[0] = ContentLibrary.NameOf(this.$store, this.$i18n.locale, moduleTotal[0])
            } catch (error) {
              console.warn('Error translating module total:', moduleTotal[0], error)
            }
          }
        })
        return filtered
      },
      chunk (array, size) {
        if (!array.length) {
          return []
        }
        const head = array.slice(0, size)
        const tail = array.slice(size)
        return [head, ...this.chunk(tail, size)]
      },
      reformatDataWithContentLibrary () {
        // Re-format daily usage data with proper module names
        if (this.usage.sessions.chartData && this.usage.sessions.chartData.length > 0) {
          const originalHeaders = this.usage.sessions.chartData[0]
          const reformattedHeaders = this.formatDialyUsageData([...originalHeaders])
          this.usage.sessions.chartData[0] = reformattedHeaders

          // Update legends
          const [, ...renderLegends] = reformattedHeaders
          this.mobileLegendsOfPlatformUsage = renderLegends
          const arrayOfLegends = this.chunk(renderLegends, 2)
          this.legendsOfPlatformUsage = arrayOfLegends
        }

        // Re-format module totals with proper module names
        if (this.usage.moduleTotals.chartData && this.usage.moduleTotals.chartData.length > 1) {
          // Create a copy to avoid mutating the original data
          const originalData = JSON.parse(JSON.stringify(this.usage.moduleTotals.chartData))
          this.usage.moduleTotals.chartData = this.formatModuleTotals(originalData)
        }
      },
      async updateDates () {
        this.menu = false
        this.startDate = moment(this.dates[0]).toDate()
        this.endDate = moment(this.dates[1]).toDate()
        await this.updateUsage()
      },
    },
  }
</script>

